import { Div, Text } from "atomize"
import { theme } from "../settings/config"
import Paragraph from "../atoms/Paragraph"
import {
	birthdayFromCF,
	formatPlayerDate,
	getPlayerAge,
	hasValidDocument,
	isMinorPlayer,
	parsePlayerDate,
} from "../utils/func.utils"

// Riga etichetta/valore: le informazioni mancanti non si mostrano proprio
function InfoRow({ label, value, valueColor }) {
	if (!value) return null

	return (
		<Div
			d="flex"
			justify="space-between"
			align="baseline"
			p={{ y: "0.3rem" }}
			style={{ gap: "1rem" }}>
			<Text textColor={theme.colors.text_light} style={{ flexShrink: 0 }}>
				{label}
			</Text>
			<Text
				textColor={valueColor ?? theme.colors.gunmetal}
				textWeight="500"
				textAlign="right"
				style={{ wordBreak: "break-word" }}>
				{value}
			</Text>
		</Div>
	)
}

// Un blocco compare solo se ha almeno un'informazione da mostrare
function InfoSection({ title, rows }) {
	const filledRows = rows.filter(row => Boolean(row.value))
	if (filledRows.length === 0) return null

	return (
		<Paragraph title={title} m={{ t: "0.75rem" }}>
			{filledRows.map(row => (
				<InfoRow key={row.label} {...row} />
			))}
		</Paragraph>
	)
}

function PlayerDetails({ playerData }) {
	// L'app non chiede più la data di nascita né il codice fiscale, ma chi li
	// ha scritti quando li chiedeva continua a vederli: se c'è solo il codice,
	// la data la si rilegge da lì invece di lasciare la riga vuota.
	const knownBirthday = playerData.birthday || birthdayFromCF(playerData.CF)
	const age = getPlayerAge(knownBirthday)
	const medCertificate = hasValidDocument(playerData, "medCertificateExpiration")
	const hasMedCertificate = Boolean(parsePlayerDate(playerData.medCertificateExpiration))

	const getMedCertificateColor = () => {
		if (!hasMedCertificate) return undefined
		if (!medCertificate.isValid) return theme.colors.red
		return medCertificate.expirationDays <= 30 ? theme.colors.warning : theme.colors.green
	}

	const getMedCertificateLabel = () => {
		if (!hasMedCertificate) return ""
		const expirationDate = formatPlayerDate(playerData.medCertificateExpiration)
		if (!medCertificate.isValid) return `${expirationDate} · scaduto`
		return expirationDate
	}

	return (
		<>
			<InfoSection
				title="Anagrafica:"
				rows={[
					{
						label: "Data di nascita",
						value: knownBirthday
							? `${formatPlayerDate(knownBirthday)}${
									age !== null ? ` · ${age} anni` : ""
								}`
							: "",
					},
					{ label: "Luogo di nascita", value: playerData.birthplace },
					{ label: "Codice fiscale", value: playerData.CF },
					{ label: "Residenza", value: playerData.residence },
				]}
			/>

			<InfoSection
				title="Contatti:"
				rows={[
					{ label: "Mail", value: playerData.mail },
					{ label: "Telefono", value: playerData.phoneNumber },
				]}
			/>

			<InfoSection
				title="Tesseramento:"
				rows={[
					{ label: "Tipo di adesione", value: playerData.memberType },
					{ label: "Taglia", value: playerData.size },
					{
						label: "Certificato medico",
						value: getMedCertificateLabel(),
						valueColor: getMedCertificateColor(),
					},
				]}
			/>

			{isMinorPlayer(knownBirthday) && (
				<InfoSection
					title="Genitore:"
					rows={[
						{
							label: "Nome",
							value: [playerData.playerParentName, playerData.playerParentSurname]
								.filter(Boolean)
								.join(" "),
						},
						{
							label: "Data di nascita",
							value: formatPlayerDate(playerData.parentBirthday),
						},
						{ label: "Luogo di nascita", value: playerData.parentBirthplace },
						{ label: "Codice fiscale", value: playerData.parentCF },
					]}
				/>
			)}
		</>
	)
}

export default PlayerDetails
